import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  LayoutDashboard,
  Scale,
  Users,
  DollarSign,
  Briefcase,
  Headphones,
  CheckCircle,
  Settings,
  BarChart3,
  Cog,
  ChevronDown,
  ChevronRight
} from 'lucide-react';
import { useAdminAuth } from '../../contexts/AdminAuthContext';

const AdminSidebar = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { hasPermission } = useAdminAuth();
  const [expandedSections, setExpandedSections] = useState(['dashboard']);

  const toggleSection = (sectionId) => {
    setExpandedSections(prev =>
      prev.includes(sectionId)
        ? prev.filter(id => id !== sectionId)
        : [...prev, sectionId]
    );
  };

  const isActive = (path) => location.pathname === path;
  const isSectionActive = (paths) => paths.some(path => location.pathname.startsWith(path));

  const menuSections = [
    {
      id: 'dashboard',
      label: 'نظرة عامة',
      labelEn: 'Overview',
      icon: LayoutDashboard,
      path: '/admin/dashboard',
      color: '#9C8862',
      permission: null,
      single: true
    },
    {
      id: 'legal',
      label: 'الإدارة القانونية',
      labelEn: 'Legal Management',
      icon: Scale,
      color: '#9C8862',
      permission: 'legal.view',
      expandable: true,
      subItems: [
        { id: 'legal-cases', label: 'القضايا', path: '/admin/legal/cases', permission: 'legal.view' },
        { id: 'legal-sessions', label: 'الجلسات', path: '/admin/legal/sessions', permission: 'legal.view' },
        { id: 'legal-execution', label: 'التنفيذ', path: '/admin/legal/execution', permission: 'legal.view' },
        { id: 'legal-documents', label: 'المذكرات والضبوط', path: '/admin/legal/documents', permission: 'legal.view' },
        { id: 'legal-decisions', label: 'القرارات', path: '/admin/legal/decisions', permission: 'legal.view' }
      ]
    },
    {
      id: 'crm',
      label: 'إدارة العملاء',
      labelEn: 'CRM',
      icon: Users,
      color: '#9C8862',
      permission: 'crm.view',
      expandable: true,
      subItems: [
        { id: 'crm-individuals', label: 'الأفراد', path: '/admin/crm/individuals', permission: 'crm.view' },
        { id: 'crm-businesses', label: 'الشركات', path: '/admin/crm/businesses', permission: 'crm.view' },
        { id: 'crm-contracts', label: 'العقود', path: '/admin/crm/contracts', permission: 'crm.view' },
        { id: 'crm-archive', label: 'الأرشيف', path: '/admin/crm/archive', permission: 'crm.view' }
      ]
    },
    {
      id: 'finance',
      label: 'الإدارة المالية',
      labelEn: 'Finance',
      icon: DollarSign,
      color: '#9C8862',
      permission: 'finance.view',
      expandable: true,
      subItems: [
        { id: 'finance-invoices', label: 'الفواتير', path: '/admin/finance/invoices', permission: 'finance.view' },
        { id: 'finance-creditors', label: 'الدائنون', path: '/admin/finance/creditors', permission: 'finance.creditors.view' },
        { id: 'finance-settings', label: 'إعدادات الفواتير', path: '/admin/finance/settings', permission: 'finance.settings' },
        { id: 'finance-revenue', label: 'الإيرادات', path: '/admin/finance/revenue', permission: 'finance.view' },
        { id: 'finance-expenses', label: 'المصروفات', path: '/admin/finance/expenses', permission: 'finance.view' },
        { id: 'finance-salaries', label: 'الرواتب', path: '/admin/finance/salaries', permission: 'finance.view' },
        { id: 'finance-reports', label: 'التقارير المالية', path: '/admin/finance/reports', permission: 'finance.view' }
      ]
    },
    {
      id: 'hr',
      label: 'الموارد البشرية',
      labelEn: 'HR',
      icon: Briefcase,
      color: '#9C8862',
      permission: 'hr.view',
      expandable: true,
      subItems: [
        { id: 'hr-employees', label: 'الموظفون', path: '/admin/hr/employees', permission: 'hr.view' },
        { id: 'hr-attendance', label: 'الحضور والانصراف', path: '/admin/hr/attendance', permission: 'hr.view' },
        { id: 'hr-salaries', label: 'الرواتب', path: '/admin/hr/salaries', permission: 'hr.view' },
        { id: 'hr-leaves', label: 'الإجازات', path: '/admin/hr/leaves', permission: 'hr.view' },
        { id: 'hr-decisions', label: 'القرارات الإدارية', path: '/admin/hr/decisions', permission: 'hr.view' }
      ]
    },
    {
      id: 'support',
      label: 'الدعم الفني',
      labelEn: 'Support',
      icon: Headphones,
      color: '#9C8862',
      permission: 'support.view',
      expandable: true,
      subItems: [
        { id: 'support-tickets', label: 'التذاكر', path: '/admin/support/tickets', permission: 'support.view' },
        { id: 'support-system', label: 'النظام', path: '/admin/support/system', permission: 'support.view' },
        { id: 'support-reports', label: 'البلاغات', path: '/admin/support/reports', permission: 'support.view' }
      ]
    },
    {
      id: 'quality',
      label: 'الجودة والامتثال',
      labelEn: 'Quality',
      icon: CheckCircle,
      color: '#9C8862',
      permission: 'quality.view',
      expandable: true,
      subItems: [
        { id: 'quality-audits', label: 'المراجعات', path: '/admin/quality/audits', permission: 'quality.view' },
        { id: 'quality-policies', label: 'السياسات', path: '/admin/quality/policies', permission: 'quality.view' },
        { id: 'quality-monitoring', label: 'المراقبة', path: '/admin/quality/monitoring', permission: 'quality.view' }
      ]
    },
    {
      id: 'operations',
      label: 'التشغيل الداخلي',
      labelEn: 'Operations',
      icon: Cog,
      color: '#9C8862',
      permission: 'operations.view',
      expandable: true,
      subItems: [
        { id: 'operations-tasks', label: 'المهام', path: '/admin/operations/tasks', permission: 'operations.view' },
        { id: 'operations-tracking', label: 'المتابعة', path: '/admin/operations/tracking', permission: 'operations.view' },
        { id: 'operations-procedures', label: 'الإجراءات', path: '/admin/operations/procedures', permission: 'operations.view' }
      ]
    },
    {
      id: 'reports',
      label: 'التقارير الذكية',
      labelEn: 'Reports',
      icon: BarChart3,
      color: '#9C8862',
      permission: 'reports.view',
      expandable: true,
      subItems: [
        { id: 'reports-legal', label: 'تقارير قانونية', path: '/admin/reports/legal', permission: 'reports.view' },
        { id: 'reports-admin', label: 'إدارية', path: '/admin/reports/admin', permission: 'reports.view' },
        { id: 'reports-financial', label: 'مالية', path: '/admin/reports/financial', permission: 'reports.view' },
        { id: 'reports-operational', label: 'تشغيلية', path: '/admin/reports/operational', permission: 'reports.view' }
      ]
    },
    {
      id: 'settings',
      label: 'الإعدادات العامة',
      labelEn: 'Settings',
      icon: Settings,
      color: '#9C8862',
      permission: 'settings.view',
      expandable: true,
      subItems: [
        { id: 'settings-permissions', label: 'الصلاحيات', path: '/admin/settings/permissions', permission: 'settings.view' },
        { id: 'settings-users', label: 'المستخدمون', path: '/admin/settings/users', permission: 'settings.view' },
        { id: 'settings-roles', label: 'الأدوار', path: '/admin/settings/roles', permission: 'settings.view' },
        { id: 'settings-logs', label: 'السجلات', path: '/admin/settings/logs', permission: 'settings.view' },
        { id: 'settings-security', label: 'الأمان', path: '/admin/settings/security', permission: '*' }
      ]
    }
  ];

  const filteredSections = menuSections.filter(section => {
    if (!section.permission) return true;
    return hasPermission(section.permission);
  });

  const renderMenuItem = (item, isSubItem = false) => {
    if (item.permission && !hasPermission(item.permission)) {
      return null;
    }

    const ItemIcon = item.icon;
    const active = isActive(item.path);
    
    return (
      <button
        key={item.id}
        onClick={() => navigate(item.path)}
        className={`w-full flex items-center gap-3 px-4 py-3 text-right transition-all rounded-lg ${
          isSubItem ? 'pr-12 text-sm' : ''
        } ${
          active
            ? 'bg-gradient-to-r from-[#9C8862] to-[#A67C52] text-white shadow-lg'
            : 'text-gray-700 hover:bg-[#9C8862]/10'
        }`}
      >
        {ItemIcon && (
          <ItemIcon 
            className={`w-${isSubItem ? '4' : '5'} h-${isSubItem ? '4' : '5'} flex-shrink-0`}
            style={{ color: active ? '#FFFFFF' : '#9C8862' }}
            strokeWidth={2.5}
          />
        )}
        <span className={`flex-1 ${active ? 'font-bold' : 'font-semibold'}`}>
          {item.label}
        </span>
        {item.badge !== undefined && item.badge > 0 && (
          <span className={`px-2 py-0.5 rounded-full text-xs font-bold ${
            active ? 'bg-[#C8A870] text-[#1E3A8A]' : 'bg-gray-200 text-gray-700'
          }`}>
            {item.badge}
          </span>
        )}
      </button>
    );
  };

  const renderSection = (section) => {
    if (section.permission && !hasPermission(section.permission)) {
      return null;
    }

    if (section.single) {
      return renderMenuItem(section);
    }

    const SectionIcon = section.icon;
    const expanded = expandedSections.includes(section.id);
    const sectionActive = isSectionActive(section.subItems?.map(item => item.path) || []);

    const visibleSubItems = section.subItems?.filter(item => 
      !item.permission || hasPermission(item.permission)
    ) || [];

    if (visibleSubItems.length === 0) {
      return null;
    }

    return (
      <div key={section.id} className="mb-1">
        <button
          onClick={() => section.expandable && toggleSection(section.id)}
          className={`w-full flex items-center gap-3 px-4 py-3 text-right transition-all rounded-lg ${
            sectionActive ? 'bg-[#9C8862]/10' : 'hover:bg-[#9C8862]/5'
          }`}
        >
          <SectionIcon 
            className="w-5 h-5 flex-shrink-0"
            style={{ color: '#9C8862' }}
            strokeWidth={2.5}
          />
          <span className="flex-1 font-bold text-[#1A1A1A]">{section.label}</span>
          {section.expandable && (
            expanded ? 
              <ChevronDown className="w-4 h-4 text-[#9C8862]" /> :
              <ChevronRight className="w-4 h-4 text-[#9C8862]" />
          )}
        </button>
        
        {section.expandable && expanded && (
          <div className="mt-1 space-y-1">
            {visibleSubItems.map(subItem => renderMenuItem(subItem, true))}
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="bg-white h-full border-l-2 border-gray-200 overflow-y-auto w-80">
      <div className="p-4">
        <div className="mb-6">
          <h2 className="text-xl font-black text-[#1E3A8A] mb-1">لوحة الإدارة</h2>
          <p className="text-xs text-gray-600">Admin Control Panel</p>
        </div>

        <div className="space-y-2">
          {filteredSections.map(section => renderSection(section))}
        </div>
      </div>
    </div>
  );
};

export default AdminSidebar;
